import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, ThumbsUp, ThumbsDown, ExternalLink, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge, ApprovalBadge, ProgressBar, Chip } from "@/components/Bits";
import { Toaster } from "@/components/ui/sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function Portal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(
    () => api.get(`/portal/${token}`).then((r) => setData(r.data)).catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Invalid link")),
    [token]
  );

  useEffect(() => { load(); }, [load]);

  const decide = async (taskId, decision) => {
    try {
      await api.post(`/portal/${token}/approve/${taskId}?decision=${encodeURIComponent(decision)}`);
      toast.success(decision === "Approved" ? "Approved — thank you!" : "Revision requested");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505] das-grid text-center px-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Link not valid</h1>
          <p className="text-zinc-500 mt-2 text-sm">{error}. Please contact Dasarro Technologies for an updated link.</p>
        </div>
      </div>
    );

  if (!data)
    return <div className="min-h-screen flex items-center justify-center bg-[#050505] text-zinc-500 font-mono text-sm">Loading project…</div>;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Toaster position="top-right" theme="dark" />
      {/* header */}
      <header className="border-b border-white/10 bg-[#0a0a0a] das-grid">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-3">
            <ShieldCheck size={13} className="text-[#00FF66]" /> Dasarro Technologies · Client Portal
          </div>
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-display text-4xl font-bold tracking-tighter">{data.brand}</h1>
              <p className="text-zinc-500 mt-1">{data.industry}</p>
            </div>
            <StatusBadge status={data.status} />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {(data.services || []).map((s) => (
              <span key={s} className="text-[10px] font-mono uppercase tracking-wider border border-white/10 bg-white/[0.03] text-zinc-400 px-2.5 py-1 rounded-sm">{s}</span>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-10 space-y-10">
        {/* pending approvals */}
        {data.pending_approvals.length > 0 && (
          <section>
            <h2 className="font-display text-xl font-medium mb-4">Awaiting your approval</h2>
            <div className="space-y-3">
              {data.pending_approvals.map((t) => (
                <div key={t.id} data-testid={`portal-approval-${t.id}`} className="bg-[#121212] border border-[#FFD600]/20 rounded-sm p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-xs font-mono text-zinc-500">{t.ref} · {t.division}</div>
                      <h3 className="font-display text-lg font-medium mt-1">{t.title}</h3>
                      {t.final_url && (
                        <a href={t.final_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-[#4DA3FF] hover:underline mt-2">
                          Review deliverable <ExternalLink size={13} />
                        </a>
                      )}
                    </div>
                    <ApprovalBadge status={t.approval_status} />
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button data-testid={`portal-approve-${t.id}`} onClick={() => decide(t.id, "Approved")} className="flex-1 flex items-center justify-center gap-2 text-sm bg-[#00FF66]/10 border border-[#00FF66]/30 text-[#00FF66] hover:bg-[#00FF66]/20 py-2.5 rounded-sm transition-colors">
                      <ThumbsUp size={15} /> Approve
                    </button>
                    <button data-testid={`portal-revise-${t.id}`} onClick={() => decide(t.id, "Revision Requested")} className="flex-1 flex items-center justify-center gap-2 text-sm bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF6259] hover:bg-[#FF3B30]/20 py-2.5 rounded-sm transition-colors">
                      <ThumbsDown size={15} /> Request Revision
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* project status */}
        <section>
          <h2 className="font-display text-xl font-medium mb-4">Project status</h2>
          <div className="space-y-2">
            {data.tasks.map((t) => (
              <div key={t.id} className="bg-[#121212] border border-white/10 rounded-sm p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-xs font-mono text-zinc-500">{t.division}</div>
                    <h3 className="text-sm text-zinc-200 truncate">{t.title}</h3>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {t.approval_status === "Approved" && <CheckCircle2 size={16} className="text-[#00FF66]" />}
                    <StatusBadge status={t.status} />
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <ProgressBar value={t.progress} />
                  <span className="text-xs font-mono text-zinc-500 w-8">{t.progress}%</span>
                </div>
              </div>
            ))}
            {data.tasks.length === 0 && <p className="text-sm text-zinc-500">No active work yet.</p>}
          </div>
        </section>

        {/* upcoming social */}
        {data.social.length > 0 && (
          <section>
            <h2 className="font-display text-xl font-medium mb-4">Upcoming content</h2>
            <div className="space-y-2">
              {data.social.map((s, i) => (
                <div key={i} className="bg-[#121212] border border-white/10 rounded-sm p-4 flex items-center justify-between gap-4">
                  <p className="text-sm text-zinc-300 line-clamp-1 flex-1">{s.caption}</p>
                  <div className="flex items-center gap-2 shrink-0">
                    <Chip className="bg-white/5 border-white/15 text-zinc-400">{s.platform}</Chip>
                    <span className="text-xs font-mono text-zinc-500">{s.scheduled_date || "—"}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <p className="text-center text-xs text-zinc-600 font-mono pt-6 border-t border-white/10">
          Powered by Dasarro Technologies · This is a private, read-only project link
        </p>
      </div>
    </div>
  );
}
